import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { confirmCode, friendlyError, sendCode } from "@/lib/firebasePhone";
import { Loader2 } from "lucide-react";

/** Verify a mobile number by SMS code; onVerified(idToken) gets the Firebase
 *  ID token to hand to the backend. `lockedPhone` (10 digits) re-verifies a
 *  known number instead of asking for one. */
export default function PhoneOtpDialog({ open, onOpenChange, title = "Verify your mobile number", lockedPhone, onVerified, recaptchaId = "phone-otp-dialog-recaptcha" }) {
  const [phone, setPhone] = useState(lockedPhone || "");
  const [code, setCode] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setPhone(lockedPhone || ""); setCode(""); setConfirmation(null); setError(""); } }, [open, lockedPhone]);

  const send = async (e) => {
    e?.preventDefault();
    if (!/^[6-9]\d{9}$/.test(phone)) return setError("Enter a 10-digit mobile number");
    setBusy(true); setError("");
    try { setConfirmation(await sendCode(phone, recaptchaId)); }
    catch (err) { setError(friendlyError(err)); }
    finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code");
    setBusy(true); setError("");
    try { await onVerified(await confirmCode(confirmation, code), phone); }
    catch (err) { setError(err.response ? (err.response.data?.detail?.message || err.response.data?.detail || "Couldn't save") : friendlyError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading text-xl">{title}</DialogTitle></DialogHeader>
        {!confirmation ? (
          <form onSubmit={send} className="space-y-3">
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-stone-50 text-sm text-stone-600">+91</span>
              <Input value={phone} readOnly={!!lockedPhone} inputMode="numeric" maxLength={10} placeholder="10-digit number"
                onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} className="h-11 rounded-l-none" data-testid="otp-dialog-phone" />
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}</Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-3">
            <Label>Code sent to +91 {phone}</Label>
            <Input value={code} inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="h-11 font-mono tracking-[0.4em]" data-testid="otp-dialog-code" />
            {error && <div className="text-sm text-destructive">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}</Button>
          </form>
        )}
        <div id={recaptchaId} />
      </DialogContent>
    </Dialog>
  );
}
